<template>
  <div class="flex justify-center bg-gray-50 py-10 px-4 sm:px-6 lg:px-8">
    <div class="max-w-md w-full">
      <div class="text-center mb-8">
        <h1 class="text-3xl font-bold text-gray-900">
          Confirm Email Change
        </h1>
      </div>

      <div class="bg-white rounded-lg shadow-md p-8">
        <!-- Loading State -->
        <div v-if="loading" class="text-center">
          <div class="inline-block animate-spin rounded-full h-12 w-12 border-b-2 border-primary mb-4"></div>
          <p class="text-gray-900">Confirming your email change...</p>
        </div>

        <!-- Success State -->
        <div v-else-if="success" class="text-center">
          <div class="mb-4">
            <svg class="mx-auto h-16 w-16 text-green-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            Email Changed Successfully!
          </h2>
          <p class="text-gray-900 mb-6">
            Your email address has been updated to <strong>{{ confirmedEmail }}</strong>.
          </p>
          <p class="text-sm text-gray-900 mb-6">
            Your subject area access has been updated based on your new email domain.
          </p>
          <d-btn class="bg-primary text-white" href="/profile">
            Go to Profile
          </d-btn>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="text-center">
          <div class="mb-4">
            <svg class="mx-auto h-16 w-16 text-red-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            Email Change Failed
          </h2>
          <d-message type="error" class="mb-6">
            {{ errorMessage }}
          </d-message>
          <div class="space-y-3">
            <p class="text-sm text-gray-900">
              Possible reasons:
            </p>
            <ul class="text-sm text-gray-900 text-left list-disc list-inside space-y-1">
              <li>The confirmation link has expired (links are valid for 24 hours)</li>
              <li>The link has already been used</li>
              <li>The link is invalid or corrupted</li>
            </ul>
          </div>
          <div class="mt-6 space-x-3">
            <d-btn class="bg-primary text-white" href="/profile">
              Go to Profile
            </d-btn>
            <d-btn outline class="border-primary text-primary" @click="retryConfirmation">
              Try Again
            </d-btn>
          </div>
        </div>

        <!-- Missing Parameters State -->
        <div v-else class="text-center">
          <div class="mb-4">
            <svg class="mx-auto h-16 w-16 text-yellow-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z"></path>
            </svg>
          </div>
          <h2 class="text-2xl font-bold text-gray-900 mb-2">
            Invalid Confirmation Link
          </h2>
          <p class="text-gray-900 mb-6">
            This confirmation link is missing required information. Please use the link from your email.
          </p>
          <d-btn class="bg-primary text-white" href="/profile">
            Go to Profile
          </d-btn>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
  import axios from 'axios';

  export default {
    name: 'ConfirmEmailChangePage',
    data() {
      return {
        loading: false,
        success: false,
        error: false,
        errorMessage: '',
        confirmedEmail: ''
      };
    },
    mounted() {
      // Parse query parameters
      const params = new URLSearchParams(window.location.search);
      const userId = params.get('userId');
      const newEmail = params.get('email');
      const token = params.get('token');

      console.log('Email confirmation params:', { userId, newEmail, token: token ? '***' : null });

      // Validate required parameters
      if (!userId || !newEmail || !token) {
        console.error('Missing required parameters');
        return;
      }

      // Attempt confirmation
      this.confirmEmailChange(userId, newEmail, token);
    },
    methods: {
      async confirmEmailChange(userId, newEmail, token) {
        this.loading = true;
        this.error = false;
        this.success = false;
        this.errorMessage = '';

        try {
          const { data } = await axios.post('/api/profile/email/confirm', {
            userId,
            newEmail,
            token
          });

          this.success = true;
          this.confirmedEmail = data.email;
        } catch (err) {
          console.error('Email confirmation error:', err);
          this.error = true;
          
          if (err.response?.data?.errors) {
            const errors = Object.values(err.response.data.errors).flat();
            this.errorMessage = errors.join(' ');
          } else if (err.response?.data?.message) {
            this.errorMessage = err.response.data.message;
          } else {
            this.errorMessage = 'An error occurred while confirming your email change. Please try again or contact support.';
          }
        } finally {
          this.loading = false;
        }
      },
      retryConfirmation() {
        const params = new URLSearchParams(window.location.search);
        const userId = params.get('userId');
        const newEmail = params.get('email');
        const token = params.get('token');
        
        if (userId && newEmail && token) {
          this.confirmEmailChange(userId, newEmail, token);
        }
      }
    }
  };
</script>
